import React from 'react';
import { motion } from 'framer-motion';
import { Check, Award, Shield, Star, Gem } from 'lucide-react';

// Tricolore component
export const Tricolore = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'h-3',
    md: 'h-4',
    lg: 'h-6',
    xl: 'h-8'
  };
  
  return (
    <div className={`flex rounded-sm overflow-hidden ${sizes[size]} ${className}`} style={{ aspectRatio: '3/2' }}>
      <div className="flex-1 bg-[#009246]" />
      <div className="flex-1 bg-white" />
      <div className="flex-1 bg-[#CE2B37]" />
    </div>
  );
};

// Tricolore stripe (horizontal line)
export const TricoloreStripe = ({ className = '' }) => (
  <div className={`flex h-1 w-full ${className}`}>
    <div className="flex-1 bg-[#009246]" />
    <div className="flex-1 bg-white" />
    <div className="flex-1 bg-[#CE2B37]" />
  </div>
);

// Made in Italy Badge
export const MadeInItalyBadge = ({ size = 'md' }) => {
  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  return (
    <div className={`inline-flex items-center gap-1.5 bg-gradient-to-r from-[#009246] via-white to-[#CE2B37] rounded-full ${sizes[size]}`}>
      <span className="bg-white px-2 py-0.5 rounded-full text-slate-800 font-semibold">
        Made in Italy
      </span>
    </div>
  );
};

// Certification badges configuration
export const CERTIFICATIONS = {
  made_in_italy: {
    label: 'Made in Italy',
    shortLabel: 'MiI',
    description: 'Produzione 100% italiana certificata',
    color: 'bg-gradient-to-r from-green-600 via-white to-red-600',
    textColor: 'text-green-800',
    icon: '🇮🇹'
  },
  artigianato_artistico: {
    label: 'Artigianato Artistico',
    shortLabel: 'AA',
    description: 'Riconoscimento regionale di artigianato artistico',
    color: 'bg-amber-100',
    textColor: 'text-amber-800',
    icon: '🎨'
  },
  eccellenza_artigiana: {
    label: 'Eccellenza Artigiana',
    shortLabel: 'EA',
    description: 'Marchio di eccellenza artigiana',
    color: 'bg-purple-100',
    textColor: 'text-purple-800',
    icon: '⭐'
  },
  bottega_storica: {
    label: 'Bottega Storica',
    shortLabel: 'BS',
    description: 'Attività storica riconosciuta',
    color: 'bg-stone-100',
    textColor: 'text-stone-800',
    icon: '🏛️'
  },
  maestro_artigiano: {
    label: 'Maestro Artigiano',
    shortLabel: 'MA',
    description: 'Qualifica di maestro artigiano',
    color: 'bg-blue-100',
    textColor: 'text-blue-800',
    icon: '👨‍🎨'
  },
  igp_prodotto: {
    label: 'IGP',
    shortLabel: 'IGP',
    description: 'Indicazione Geografica Protetta',
    color: 'bg-yellow-100',
    textColor: 'text-yellow-800',
    icon: '🏷️'
  },
  dop_prodotto: {
    label: 'DOP',
    shortLabel: 'DOP',
    description: 'Denominazione di Origine Protetta',
    color: 'bg-red-100',
    textColor: 'text-red-800',
    icon: '🍷'
  },
  bio_certificato: {
    label: 'Bio Certificato',
    shortLabel: 'BIO',
    description: 'Certificazione biologica europea',
    color: 'bg-green-100',
    textColor: 'text-green-800',
    icon: '🌿'
  }
};

// Single certification badge
export const CertificationBadge = ({ certification, size = 'md', showLabel = true }) => {
  const cert = CERTIFICATIONS[certification];
  if (!cert) return null;
  
  const sizes = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  return (
    <motion.div 
      className={`inline-flex items-center gap-1 rounded-full ${cert.color} ${cert.textColor} ${sizes[size]} font-medium`}
      whileHover={{ scale: 1.05 }}
      title={cert.description}
    >
      <span>{cert.icon}</span>
      {showLabel && <span>{size === 'sm' ? cert.shortLabel : cert.label}</span>}
    </motion.div>
  );
};

// Rarifex Verified Badge
export const RarifexVerifiedBadge = ({ size = 'md' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };
  
  return (
    <motion.div 
      className={`inline-flex items-center justify-center ${sizes[size]} bg-[#008080] rounded-full`}
      whileHover={{ scale: 1.1 }}
      title="Verificato da Rarifex"
    >
      <Check className="w-3 h-3 text-white" />
    </motion.div>
  );
};

// Full Rarifex Verified Badge with text
export const RarifexVerifiedBadgeFull = ({ size = 'md' }) => {
  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  return (
    <motion.div 
      className={`inline-flex items-center gap-1.5 bg-[#008080] text-white rounded-full ${sizes[size]} font-medium`}
      whileHover={{ scale: 1.02 }}
      title="Questo artigiano è stato verificato dal team Rarifex"
    >
      <Shield className="w-4 h-4" />
      <span>Verificato Rarifex</span>
    </motion.div>
  );
};

// Crafter type badge
export const CrafterTypeBadge = ({ type, size = 'md' }) => {
  const isProf = type === 'professionista';
  
  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5'
  };
  
  return (
    <div 
      className={`inline-flex items-center gap-1.5 rounded-full ${sizes[size]} font-medium ${
        isProf 
          ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white' 
          : 'bg-slate-100 text-slate-700'
      }`}
    >
      {isProf ? <Award className="w-4 h-4" /> : <Star className="w-4 h-4" />}
      <span>{isProf ? 'Artigiano Professionista' : 'Hobbista'}</span>
    </div>
  );
};

// All badges display component
export const CrafterBadges = ({ profile, size = 'md', showAll = false }) => {
  if (!profile) return null;
  
  const certifications = profile.certifications || [];
  const displayCerts = showAll ? certifications : certifications.slice(0, 3);
  const hasMore = !showAll && certifications.length > 3;
  
  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Crafter Type */}
      {profile.crafter_type && (
        <CrafterTypeBadge type={profile.crafter_type} size={size} />
      )}
      
      {/* Rarifex Verified */}
      {profile.rarifex_verified && (
        <RarifexVerifiedBadgeFull size={size} />
      )}
      
      {/* Certifications */}
      {displayCerts.map((cert, idx) => (
        <CertificationBadge key={idx} certification={cert} size={size} />
      ))}
      
      {hasMore && (
        <span className="text-sm text-slate-500">+{certifications.length - 3}</span>
      )}
    </div>
  );
};

export default {
  Tricolore,
  TricoloreStripe,
  MadeInItalyBadge,
  CertificationBadge,
  RarifexVerifiedBadge,
  RarifexVerifiedBadgeFull,
  CrafterTypeBadge,
  CrafterBadges,
  CERTIFICATIONS
};
